import {
  useState,
  useRef,
  useMemo,
  DragEvent,
  ChangeEvent,
} from 'react';
import { alpha, styled } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import UploadDocumentIcon from '@/assets/upload-document.svg?react';
import Button from '@/components/Button/Button';

const getFileExtension = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  if (dot === -1 || dot === fileName.length - 1) return '';
  return fileName.slice(dot + 1).toLowerCase();
};

const normalizeExtension = (extension: string): string =>
  extension || 'unknown';

/** Path is relative to the upload root, e.g. `A/C/d.txt`. */
export type PickedFile = {
  file: File;
  relativePath: string;
};

const filterFilesByExtension = (
  files: PickedFile[],
  acceptedExtensions: string[],
): PickedFile[] => {
  const allowed = new Set(
    acceptedExtensions.map((extension) => extension.toLowerCase()),
  );

  return files.filter((picked) =>
    allowed.has(normalizeExtension(getFileExtension(picked.file.name))),
  );
};

const joinPath = (prefix: string, name: string): string =>
  prefix ? `${prefix}/${name}` : name;

/** Only folder sources are swept: picking a dot-prefixed file by hand is deliberate. */
const isHidden = (relativePath: string): boolean =>
  relativePath.split('/').some((segment) => segment.startsWith('.'));

/** The picker yields one folder and means "upload its contents", so its own name is dropped. */
const relativePathFromInput = (file: File): string => {
  const webkitPath = file.webkitRelativePath;
  if (!webkitPath) return file.name;
  const [, ...insideRoot] = webkitPath.split('/');
  return insideRoot.length ? insideRoot.join('/') : file.name;
};

const readAllDirectoryEntries = (
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> =>
  new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    // One batch (~100 entries) per call; reading once would truncate a large folder.
    const readBatch = () =>
      reader.readEntries((entries) => {
        if (entries.length === 0) {
          resolve(all);
          return;
        }
        all.push(...entries);
        readBatch();
      }, reject);
    readBatch();
  });

const entryToFile = (entry: FileSystemFileEntry): Promise<File> =>
  new Promise((resolve, reject) => entry.file(resolve, reject));

const collectEntry = async (
  entry: FileSystemEntry,
  prefix: string,
): Promise<PickedFile[]> => {
  if (entry.name.startsWith('.')) return [];

  if (entry.isFile) {
    const file = await entryToFile(entry as FileSystemFileEntry);
    return [{ file, relativePath: joinPath(prefix, file.name) }];
  }
  if (!entry.isDirectory) return [];

  const children = await readAllDirectoryEntries(
    (entry as FileSystemDirectoryEntry).createReader(),
  );
  const collected: PickedFile[] = [];
  for (const child of children) {
    // One unreadable child must not cost the user its siblings.
    try {
      collected.push(
        ...(await collectEntry(child, joinPath(prefix, entry.name))),
      );
    } catch (error) {
      console.error(
        `[dropzone] skipped "${joinPath(prefix, child.name)}": could not be read`,
        error,
      );
    }
  }
  return collected;
};

const extensionsToAcceptAttribute = (extensions: string[]): string =>
  extensions.map((extension) => `.${extension}`).join(',');

const extensionsToDisplayLabel = (extensions: string[]): string =>
  extensions.map((extension) => extension.toUpperCase()).join(', ');

export interface DropZoneBigProps {
  onFilesChange: (files: PickedFile[]) => void;
  acceptedExtensions?: string[];
  children?: React.ReactNode;
  busy?: boolean;
  /** Adds a folder picker and walks dropped folders instead of discarding them. */
  allowDirectories?: boolean;
}

export const DropZoneBig = ({
  onFilesChange,
  acceptedExtensions,
  children,
  busy = false,
  allowDirectories = false,
}: DropZoneBigProps) => {
  const intl = useIntl();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const skipClickAfterDropRef = useRef(false);
  const acceptAttribute = useMemo(
    () =>
      acceptedExtensions?.length
        ? extensionsToAcceptAttribute(acceptedExtensions)
        : undefined,
    [acceptedExtensions],
  );
  const fileTypesLabel = useMemo(
    () =>
      acceptedExtensions?.length
        ? extensionsToDisplayLabel(acceptedExtensions)
        : '',
    [acceptedExtensions],
  );

  const acceptedOnly = (files: PickedFile[]): PickedFile[] =>
    acceptedExtensions?.length
      ? filterFilesByExtension(files, acceptedExtensions)
      : files;

  const filesFromList = (
    fileList: FileList | null,
    { fromFolder = false }: { fromFolder?: boolean } = {},
  ): PickedFile[] => {
    if (!fileList?.length) return [];
    const picked = Array.from(fileList).map((file) => ({
      file,
      relativePath: relativePathFromInput(file),
    }));
    return acceptedOnly(
      fromFolder
        ? picked.filter((entry) => !isHidden(entry.relativePath))
        : picked,
    );
  };

  const emitFiles = (files: PickedFile[]) => {
    if (busy || files.length === 0) return;
    onFilesChange(files);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const target = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;

    if (!target.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    skipClickAfterDropRef.current = true;
    window.setTimeout(() => {
      skipClickAfterDropRef.current = false;
    }, 400);

    if (busy) return;

    // The DataTransfer is emptied as soon as this handler yields.
    const entries = allowDirectories
      ? Array.from(e.dataTransfer.items)
          .map((item) => item.webkitGetAsEntry?.() ?? null)
          .filter((entry): entry is FileSystemEntry => entry !== null)
      : [];

    if (entries.length === 0) {
      emitFiles(filesFromList(e.dataTransfer.files));
      return;
    }

    // Each dropped entry starts at an empty prefix, so a dropped folder keeps its own name.
    void (async () => {
      const collected: PickedFile[] = [];
      for (const entry of entries) {
        try {
          collected.push(...(await collectEntry(entry, '')));
        } catch (error) {
          console.error(
            `[dropzone] skipped "${entry.name}": could not be read`,
            error,
          );
        }
      }
      emitFiles(acceptedOnly(collected));
    })();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fromFolder = e.target === folderInputRef.current;
    emitFiles(filesFromList(e.target.files, { fromFolder }));
    e.target.value = '';
  };

  const openFilePicker = () => {
    if (busy) return;
    if (skipClickAfterDropRef.current) return;
    inputRef.current?.click();
  };

  const openFolderPicker = () => {
    if (busy) return;
    if (skipClickAfterDropRef.current) return;
    folderInputRef.current?.click();
  };

  return (
    <Root>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        disabled={busy}
        accept={acceptAttribute}
        onChange={handleInputChange}
      />
      {allowDirectories ? (
        // `webkitdirectory` has no typed React prop; `accept` is useless on a whole tree.
        <input
          ref={(el) => {
            folderInputRef.current = el;
            if (el) {
              el.setAttribute('webkitdirectory', '');
              el.setAttribute('directory', '');
            }
          }}
          type="file"
          multiple
          hidden
          disabled={busy}
          onChange={handleInputChange}
        />
      ) : null}
      <DropSurface
        $isDragging={isDragging}
        $busy={busy}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={openFilePicker}
        role="group"
        aria-label={intl.formatMessage(
          allowDirectories
            ? {
                id: 'dropZoneBig.ariaUploadWithFolders',
                defaultMessage:
                  'Select or drop files and folders to upload',
              }
            : {
                id: 'dropZoneBig.ariaUpload',
                defaultMessage: 'Select or drop files to upload',
              },
        )}
      >
        <IconWrap>
          <UploadDocumentIcon />
        </IconWrap>
        <PrimaryLabel>
          {allowDirectories ? (
            <FormattedMessage
              id="dropZoneBig.dragYourFilesOrFoldersHere"
              defaultMessage="Drag your file(s) or folder(s) here"
            />
          ) : (
            <FormattedMessage
              id="dropZoneBig.dragYourFilesHere"
              defaultMessage="Drag your file(s) here"
            />
          )}
        </PrimaryLabel>
        {fileTypesLabel ? (
          <SecondaryLabel>{fileTypesLabel}</SecondaryLabel>
        ) : null}
        <OrRow>
          <OrLine aria-hidden />
          <OrText>
            <FormattedMessage id="dropZoneBig.or" defaultMessage="OR" />
          </OrText>
          <OrLine aria-hidden />
        </OrRow>
        <BrowseWrap>
          <Button
            type="button"
            variant="outlined"
            loading={busy}
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              openFilePicker();
            }}
            aria-label={intl.formatMessage({
              id: 'dropZoneBig.browseFiles',
              defaultMessage: 'Browse Files',
            })}
          >
            <FormattedMessage
              id="dropZoneBig.browseFiles"
              defaultMessage="Browse Files"
            />
          </Button>
          {allowDirectories ? (
            <Button
              type="button"
              variant="outlined"
              loading={busy}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                openFolderPicker();
              }}
              aria-label={intl.formatMessage({
                id: 'dropZoneBig.browseFolders',
                defaultMessage: 'Browse Folders',
              })}
            >
              <FormattedMessage
                id="dropZoneBig.browseFolders"
                defaultMessage="Browse Folders"
              />
            </Button>
          ) : null}
        </BrowseWrap>
      </DropSurface>
      {children ? <SecondPart>{children}</SecondPart> : null}
    </Root>
  );
};

const Root = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

const DropSurface = styled('div')<{ $isDragging: boolean; $busy: boolean }>(
  ({ theme, $isDragging, $busy }) => ({
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(2.5)}`,
    border: `1px dashed ${theme.palette.grey400}`,
    borderRadius: theme.radii.xs,
    cursor: $busy ? 'not-allowed' : 'pointer',
    outline: 'none',
    backgroundColor: $isDragging
      ? alpha(theme.palette.blue500, 0.06)
      : 'transparent',
    transition: 'background-color 0.15s ease',
    pointerEvents: $busy ? 'none' : 'auto',
    opacity: $busy ? 0.65 : 1,
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.blue500, 0.35)}`,
    },
  }),
);

const IconWrap = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1.5),
  color: theme.palette.primary.main,
  '& svg': {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
  },
}));

const PrimaryLabel = styled('div')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.black80o,
  textAlign: 'center',
  marginBottom: theme.spacing(0.5),
}));

const SecondaryLabel = styled('div')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.black40o,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const OrRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
  maxWidth: 280,
}));

const OrLine = styled('div')(({ theme }) => ({
  width: 80,
  height: 1,
  flexShrink: 0,
  backgroundColor: theme.palette.black40o,
}));

const OrText = styled('div')(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.black40o,
  flexShrink: 0,
}));

const BrowseWrap = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  justifyContent: 'center',
}));

const SecondPart = styled('div')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(2),
}));

export default DropZoneBig;
